import React, { useState } from "react";
import { InfoCircleFilled } from '@ant-design/icons';
import { AddBeneficiaryModal } from '../../components/AddBeneficiaryModal';
import { useDeleteBeneficiary } from '../../hooks/beneficiaries/useDeleteBeneficiary';

// Tooltip component for info icon
function InfoTooltip({ info }) {
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <InfoCircleFilled
        style={{ color: '#111', fontSize: 17, marginLeft: 4, cursor: 'pointer', verticalAlign: 'middle' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onTouchStart={() => setShow(v => !v)}
      />
      {show && (
        <span style={{
          position: 'absolute',
          left: '50%',
          top: '120%',
          transform: 'translateX(-50%)',
          background: '#222',
          color: '#fff',
          padding: '7px 14px',
          borderRadius: 7,
          fontSize: 13,
          whiteSpace: 'nowrap',
          zIndex: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          {info}
        </span>
      )}
    </span>
  );
}

function formatCurrency(value) {
  if (!value) return '';
  const num = Number(String(value).replace(/[^\d.]/g, ''));
  if (isNaN(num)) return value;
  return `GHS ${num.toLocaleString()}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function MaxInvestmentsPage() {
  // Example data (replace with real data as needed)
  const productDetails = {
    policyName: "Old Mutual Investment Plan",
    policyNumber: "INV-2025-001",
    status: "Active",
    totalPremiumPaid: 12000,
    modalPremium: 1000,
    premiumAmount: 2000,
    premiumFrequency: "Monthly",
    totalRedemption: 2500,
    startDate: "2025-07-07",
    endDate: "2028-07-07"
  };
  const [beneficiaries, setBeneficiaries] = useState([
    {
      name: "John Doe",
      percentage: "50%",
      relationship: "Brother",
      dob: "1990-05-10"
    },
    {
      name: "Jane Smith",
      percentage: "50%",
      relationship: "Sister",
      dob: "1992-08-15"
    }
  ]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activeBeneficiary, setActiveBeneficiary] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const deleteMutation = useDeleteBeneficiary();

  const handleOpenEdit = (b) => {
    setActiveBeneficiary(b);
    setIsEdit(true);
    setAddModalOpen(true);
  };

  const handleOpenDelete = (b) => {
    setActiveBeneficiary(b);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setActiveBeneficiary(null);
  };

  const confirmDelete = async () => {
    if (!activeBeneficiary) return;
    try {
      await deleteMutation.mutateAsync({ beneficiaryId: activeBeneficiary.beneficiaryId || activeBeneficiary.name });
      setBeneficiaries((prev) => prev.filter(b => b !== activeBeneficiary));
      setDeleteModalOpen(false);
      setActiveBeneficiary(null);
    } catch (err) {
      // notify handled in hook
    }
  };
  return (
    <div style={{ minHeight: '80vh', background: '#f5f6fa', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32 }}>

      <div style={{ width: '100%', maxWidth: 700, display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center' }}>
        {/* Product Details Card */}
        <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)', padding: '32px 48px', width: '100%', display: 'flex', flexDirection: 'column', gap: 0, maxWidth: 700 }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: '#000000ff', marginBottom: 12 }}>Product Details</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{productDetails.policyName}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Policy Name</div>
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{productDetails.policyNumber}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Policy Number</div>
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#000000ff', fontWeight: 700, fontSize: 18 }}>{productDetails.status}</span>
                <InfoTooltip info={
                  productDetails.status === 'Active'
                    ? 'This policy is currently active and in good standing.'
                    : productDetails.status === 'Inactive'
                      ? 'This policy is not currently active.'
                      : 'Status information about this policy.'
                } />
              </div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Policy Status</div>
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{formatCurrency(productDetails.totalPremiumPaid)}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Total Premium Paid</div>
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{formatCurrency(productDetails.modalPremium)}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Modal Premium</div>
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{formatCurrency(productDetails.totalRedemption)}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Total Redemption</div>
            </div>
            {/* Premium Amount Row */}
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{formatCurrency(productDetails.premiumAmount)}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Premium Amount</div>
            </div>
            {/* Premium Frequency Row */}
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{productDetails.premiumFrequency}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Premium Frequency</div>
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{formatDate(productDetails.startDate)}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Premium Start Date</div>
            </div>
            <div style={{ borderTop: '1px solid #eee', padding: '10px 0', textAlign: 'left' }}>
              <div style={{ color: '#444', fontWeight: 700, fontSize: 18 }}>{formatDate(productDetails.endDate)}</div>
              <div style={{ color: '#888', fontWeight: 500, fontSize: 13, marginTop: 2 }}>Premium End Date</div>
            </div>
          </div>
        </div>
        {/* Beneficiary Card */}
        <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)', padding: '32px 48px', width: '100%', display: 'flex', flexDirection: 'column', gap: 0, maxWidth: 700 }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: '#000000ff', marginBottom: 12 }}>Beneficiaries</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {beneficiaries.map((b, idx) => {
              const initials = b.name.split(' ').map(n => n[0]).join('').toUpperCase();
              return (
                <div className="beneficiary-row"
                  key={b.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderTop: idx !== 0 ? '1px solid #eee' : undefined,
                    padding: '18px 0',
                    textAlign: 'left',
                    position: 'relative',
                    transition: 'background 0.2s, border-radius 0.2s',
                    cursor: 'pointer',
                    borderRadius: 0,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#e6f9ed';
                    e.currentTarget.style.borderRadius = '12px';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '';
                    e.currentTarget.style.borderRadius = '0';
                  }}
                >
                  {/* Profile left */}
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: 20, marginRight: 18, flexShrink: 0 }}>
                    {initials}
                  </div>
                  {/* Details center */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, color: '#222', fontSize: 18, lineHeight: 1.2 }}>{b.name}</div>
                    <div style={{ color: '#888', fontWeight: 500, fontSize: 15, marginTop: 2 }}>{b.percentage} Split</div>
                    <div style={{ color: '#888', fontWeight: 500, fontSize: 15, marginTop: 2, display: 'flex', alignItems: 'center', gap: 18 }}>
                      <span>{b.relationship}</span>
                      <span style={{ fontSize: 17, fontWeight: 400, color: '#bbb' }}>-</span>
                      <span>{b.dob ? formatDate(b.dob) : ''}</span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div style={{ marginLeft: 18, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-white"
                      aria-label="Edit beneficiary"
                      style={{ padding: '6px 10px', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={(event) => { event.stopPropagation(); handleOpenEdit(b); }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-white"
                      aria-label="Delete beneficiary"
                      style={{ padding: '6px 10px', fontSize: 14, fontWeight: 600, color: '#C62828', borderColor: '#C62828', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={(event) => { event.stopPropagation(); handleOpenDelete(b); }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <style>{`
          .beneficiary-row { width: 100%; }
          .beneficiary-row div { box-sizing: border-box; }
          @media (max-width: 640px) {
            .beneficiary-row { flex-direction: column !important; align-items: flex-start !important; gap: 10px; }
            .beneficiary-row > div:first-child { width: 40px !important; height: 40px !important; font-size: 16px !important; margin-right: 12px !important; }
            .beneficiary-row .btn { padding: 8px 10px !important; font-size: 13px !important; }
            .beneficiary-row .btn[aria-label="Edit beneficiary"], .beneficiary-row .btn[aria-label="Delete beneficiary"] { margin-right: 6px; }
            .beneficiary-row div[style*="flex: 1"] { width: 100%; }
          }
        `}</style>
        <AddBeneficiaryModal
          isOpen={addModalOpen}
          onClose={() => { setAddModalOpen(false); setIsEdit(false); setActiveBeneficiary(null); }}
          currentTotalPerc={0}
          isEdit={isEdit}
          beneficiaryToEdit={activeBeneficiary}
          onEditSuccess={(updatedBeneficiary) => {
            setBeneficiaries((prev) => prev.map((b) => b === activeBeneficiary ? updatedBeneficiary : b));
            setAddModalOpen(false);
            setActiveBeneficiary(null);
            setIsEdit(false);
          }}
        />
        {deleteModalOpen && activeBeneficiary && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.18)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
            onClick={closeDeleteModal}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 16,
                width: '100%',
                maxWidth: 460,
                padding: '28px 32px',
                boxShadow: '0 16px 60px rgba(0,0,0,0.15)'
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 10 }}>Remove Beneficiary</div>
              <p style={{ color: '#666', fontSize: 15, lineHeight: 1.4 }}>
                Are you sure you want to remove <strong>{activeBeneficiary.name}</strong> from this policy? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
                <button type="button" className="btn btn-white" onClick={closeDeleteModal} style={{ padding: '10px 18px', fontWeight: 600 }}>Keep Beneficiary</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ padding: '10px 18px', fontWeight: 600, background: '#C62828', color: '#fff', borderColor: '#C62828' }}
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MaxInvestmentsPage;